import React, { useState } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

const ChangePassword = ({ embedded = false }) => {
  const { changePassword } = useAuth();
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    old_password: '',
    new_password: '',
    new_password_confirm: '',
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
    setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    if (formData.new_password !== formData.new_password_confirm) {
      setError(t('security.passwordMismatch'));
      setLoading(false);
      return;
    }

    try {
      await changePassword(
        formData.old_password,
        formData.new_password,
        formData.new_password_confirm,
      );
      setMessage(t('security.success'));
      setFormData({
        old_password: '',
        new_password: '',
        new_password_confirm: '',
      });
    } catch (err) {
      const errorText = err.data?.errorText;
      if (typeof errorText === 'string') {
        setError(errorText);
      } else if (typeof errorText === 'object') {
        const messages = Object.entries(errorText)
          .map(([field, msgs]) => {
            const fieldName = field.replace(/_/g, ' ');
            const msgList = Array.isArray(msgs) ? msgs : [msgs];
            return msgList.map((m) => `${fieldName}: ${m}`);
          })
          .flat();
        setError(messages.join('. '));
      } else {
        setError(t('security.changeFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const card = (
    <Card className="border-0 shadow-sm">
      <Card.Header className="bg-white border-bottom py-3">
        <h5 className="mb-0 fw-bold">{t('security.changePassword')}</h5>
      </Card.Header>
      <Card.Body className="p-4">
        {message && <Alert variant="success">{message}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="text-muted small">{t('security.currentPassword')}</Form.Label>
            <div className="position-relative">
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                name="old_password"
                value={formData.old_password}
                onChange={handleChange}
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                <i className={`bi bi-eye${showPassword ? '-slash' : ''}`} />
              </button>
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="text-muted small">{t('security.newPassword')}</Form.Label>
            <Form.Control
              type="password"
              name="new_password"
              value={formData.new_password}
              onChange={handleChange}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label className="text-muted small">{t('security.confirmPassword')}</Form.Label>
            <Form.Control
              type="password"
              name="new_password_confirm"
              value={formData.new_password_confirm}
              onChange={handleChange}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </Form.Group>

          <Alert variant="light" className="border">
            <h6 className="mb-2 fw-bold">
              <i className="bi bi-info-circle me-2" />
              {t('security.requirementsTitle')}
            </h6>
            <ul className="mb-0 ps-3 small text-muted">
              <li>{t('security.reqLength')}</li>
              <li>{t('security.reqSimilar')}</li>
              <li>{t('security.reqCommon')}</li>
              <li>{t('security.reqNumeric')}</li>
            </ul>
          </Alert>

          <div className="d-flex justify-content-end mt-4">
            <Button type="submit" className="btn-gradient" disabled={loading}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {t('security.updating')}
                </>
              ) : (
                t('security.update')
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );

  if (embedded) {
    return <div className="chat-app-profile-embed">{card}</div>;
  }
  return card;
};

export default ChangePassword;
